import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { useAccounts } from '../../../context/AccountContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { Card } from '../../../components/ui/Card';
import {
  RefreshCw,
  Loader2,
  AlertCircle,
  Eye,
  MousePointerClick,
  Image as ImageIcon,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

import { isDemoBrand, demoHash } from '../../../utils/demoBrand';

const API_URL = 'https://api.glowytics.com';

const nf = new Intl.NumberFormat('tr-TR');

// Static "today's impressions" payload for demo brands. Deterministic per campaign
// so numbers stay stable across refreshes; picsum images stand in for creatives.
function buildDemoTodayImpressions() {
  const campaignDefs = [
    { name: 'ADV+ Alışveriş | Prospecting', ads: 6 },
    { name: 'Retargeting | 30G Site Ziyaretçileri', ads: 5 },
    { name: 'Lookalike %3 | Satın Alma', ads: 4 },
    { name: 'Marka Bilinirliği | Reels', ads: 3 },
  ];
  const headlines = ['Yeni Sezon İndirimleri', 'Sınırlı Stok', 'Ücretsiz Kargo', 'Sana Özel %20', 'Çok Satanlar', 'Hemen Keşfet'];
  const texts = [
    'Sezonun en çok tercih edilen ürünleri şimdi indirimde. Kaçırma!',
    'Bugüne özel fırsatlarla tarzını yenile.',
    '2.500₺ üzeri alışverişlerde kargo bedava.',
    'Sepette ekstra indirim seni bekliyor.',
  ];
  const campaigns = campaignDefs.map((c, ci) => {
    const ads = Array.from({ length: c.ads }).map((_, ai) => {
      const h = demoHash(`${c.name}-${ai}`);
      const impressions = 1800 + (h % 14000);
      const clicks = Math.round(impressions * (0.012 + (h % 25) / 1000));
      const spend = Math.round(impressions * (0.9 + (h % 60) / 100) / 10);
      return {
        ad_id: `demo-${ci}-${ai}`,
        ad_name: `${c.name.split('|')[0].trim()} — Creative ${ai + 1}`,
        creative_name: `Creative ${ai + 1}`,
        image_url: `https://picsum.photos/seed/gwmeta${ci}${ai}/400/400`,
        headline: headlines[h % headlines.length],
        primary_text: texts[h % texts.length],
        description: 'glowytics.com',
        impressions,
        clicks,
        spend,
      };
    });
    const sum = (f) => ads.reduce((s, a) => s + a[f], 0);
    return {
      campaign_id: `demo-camp-${ci}`,
      campaign_name: c.name,
      ads,
      impressions: sum('impressions'),
      clicks: sum('clicks'),
      spend: sum('spend'),
    };
  });
  const items = campaigns.flatMap((c) => c.ads);
  const today = new Date().toISOString().split('T')[0];
  return {
    date: today,
    total_ads: items.length,
    total_campaigns: campaigns.length,
    items,
    campaigns,
  };
}
const fmtSpend = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v || 0);

export default function MetaTodayImpressions() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const brandId = selectedBrand?.id;

  const fetchData = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedAccountId && selectedAccountId !== 'all') params.accountId = selectedAccountId;
      const resp = await axios.get(`${API_URL}/api/meta-ads/live/today-impressions`, {
        params,
        headers: { 'X-Brand-ID': brandId },
        withCredentials: true,
        timeout: 120000
      });
      let payload = resp.data;
      if (isDemoBrand(selectedBrand) && (!payload || (payload.campaigns || []).length === 0)) {
        payload = buildDemoTodayImpressions();
      }
      setData(payload);
      // expand all campaigns by default
      const exp = {};
      (payload?.campaigns || []).forEach((c) => {
        exp[c.campaign_id || c.campaign_name] = true;
      });
      setExpanded(exp);
    } catch (err) {
      if (isDemoBrand(selectedBrand)) {
        const payload = buildDemoTodayImpressions();
        setData(payload);
        const exp = {};
        payload.campaigns.forEach((c) => { exp[c.campaign_id || c.campaign_name] = true; });
        setExpanded(exp);
        setError(null);
      } else {
        setError(err.response?.data?.error || err.message || 'Veri alınamadı');
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [brandId, selectedAccountId, selectedBrand]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (!selectedBrand) {
    return (
      <div className="p-6 text-gray-500">Lütfen önce bir marka seçin.</div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-6 h-6 text-blue-600" />
            Bugün Gösterim Alan Görseller
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {selectedBrand?.name} · Meta'dan canlı çekilen, bugün en az 1 gösterim alan tüm reklam görselleri ve kampanyaları
            {data?.date ? ` · ${data.date}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AccountSwitcher platform="META_ADS" />
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>
      </div>

      {/* Summary */}
      {data && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Card className="p-4">
            <div className="text-xs text-gray-500">Görsel / Reklam</div>
            <div className="text-2xl font-bold text-gray-900">{nf.format(data.total_ads || 0)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-gray-500">Kampanya</div>
            <div className="text-2xl font-bold text-gray-900">{nf.format(data.total_campaigns || 0)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-gray-500">Toplam Gösterim</div>
            <div className="text-2xl font-bold text-gray-900">
              {nf.format((data.items || []).reduce((s, i) => s + (i.impressions || 0), 0))}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-gray-500">Toplam Harcama</div>
            <div className="text-2xl font-bold text-gray-900">
              {fmtSpend((data.items || []).reduce((s, i) => s + (i.spend || 0), 0))}
            </div>
          </Card>
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mb-3 text-blue-600" />
          <p>Meta'dan canlı veri çekiliyor…</p>
          <p className="text-xs text-gray-400 mt-1">Görsel sayısına göre birkaç saniye sürebilir.</p>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {data && !loading && (data.campaigns || []).length === 0 && (
        <div className="text-center py-20 text-gray-500">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          Bugün henüz gösterim alan bir reklam yok.
        </div>
      )}

      {/* Campaign groups */}
      {data && !loading && (data.campaigns || []).map((camp) => {
        const key = camp.campaign_id || camp.campaign_name;
        const isOpen = expanded[key];
        return (
          <div key={key} className="mb-4 border border-gray-200 rounded-xl overflow-hidden bg-white">
            <button
              onClick={() => toggle(key)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                <span className="font-semibold text-gray-900 truncate">{camp.campaign_name}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">({camp.ads.length} görsel)</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 flex-shrink-0">
                <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{nf.format(camp.impressions)}</span>
                <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" />{nf.format(camp.clicks)}</span>
                <span className="font-medium">{fmtSpend(camp.spend)}</span>
              </div>
            </button>

            {isOpen && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
                {camp.ads.map((ad) => (
                  <div key={ad.ad_id} className="border border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                      {ad.image_url ? (
                        <img
                          src={ad.image_url}
                          alt={ad.ad_name || ad.creative_name || 'creative'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentNode.querySelector('.fallback').style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className="fallback w-full h-full items-center justify-center text-gray-300" style={{ display: ad.image_url ? 'none' : 'flex' }}>
                        <ImageIcon className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-medium text-gray-800 truncate" title={ad.ad_name}>{ad.ad_name || ad.creative_name || ad.ad_id}</div>

                      {/* Reklam metinleri */}
                      {(ad.primary_text || ad.headline || ad.description) ? (
                        <div className="mt-1.5 space-y-1">
                          {ad.headline && (
                            <div className="text-[11px] font-semibold text-gray-700 line-clamp-2" title={ad.headline}>{ad.headline}</div>
                          )}
                          {ad.primary_text && (
                            <div className="text-[11px] text-gray-600 line-clamp-3 whitespace-pre-line" title={ad.primary_text}>{ad.primary_text}</div>
                          )}
                          {ad.description && (
                            <div className="text-[10px] text-gray-400 line-clamp-2" title={ad.description}>{ad.description}</div>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1.5 text-[10px] text-gray-300 italic">Metin yok</div>
                      )}

                      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100 text-[11px] text-gray-500">
                        <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" />{nf.format(ad.impressions)}</span>
                        <span>{fmtSpend(ad.spend)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
